/*
Phone controls of the releases calendar (≤739px, the width releases.css switches at): the
model type sheet — a native modal <dialog>, page-releases.hbs — the month select by the title,
and page scrolling to a day of the days view. releasesPage.js decides what is shown; this
module only opens, fills and scrolls.
*/
import { monthLabel } from './releasesData';

/** Phone layout: the dock, the days view, the type sheet. Same width as in releases.css. */
export const PHONE = window.matchMedia('(max-width: 739px)');

const openers = new WeakMap();

/**
 * Wires the type sheet of the page: [data-open-types] opens it with focus on the chosen
 * type, [data-close-types], Esc and a tap on the backdrop close it; focus goes back to the
 * button that opened it. A type row is a data-set control handled by the page.
 * @param {HTMLElement} root the calendar element holding the sheet and its openers
 */
export function bindTypeSheet(root) {
    const sheet = root.querySelector('.th-rel-sheet');
    if (!sheet || typeof sheet.showModal !== 'function') {
        return;
    }
    root.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-open-types]');
        if (opener) {
            openers.set(sheet, opener);
            sheet.showModal();
            sheet.querySelector('[aria-pressed="true"]')?.focus();
        } else if (event.target.closest('[data-close-types]') || event.target === sheet) {
            sheet.close();
        }
    });
    sheet.addEventListener('close', () => {
        const opener = openers.get(sheet);
        if (opener?.isConnected && opener.offsetParent !== null) {
            opener.focus();
        }
    });
}

/** Closes the type sheet if it is open (a type chosen, the screen turned wide). */
export function closeTypeSheet(root) {
    const sheet = root.querySelector('.th-rel-sheet');
    if (sheet?.open) {
        sheet.close();
    }
}

/**
 * Month select options: the months of the view, "oct 2026"; rebuilt only when the months
 * change, so an open select is not reset by a render.
 * @param {HTMLSelectElement} select
 * @param {string[]} months YYYY-MM, oldest first
 * @param {string} chosen YYYY-MM
 */
export function fillMonthSelect(select, months, chosen) {
    const key = months.join(',');
    if (select.dataset.months !== key) {
        select.replaceChildren(...months.map((month) => monthOption(month)));
        select.dataset.months = key;
    }
    select.value = chosen;
}

function monthOption(month) {
    const option = document.createElement('option');
    option.value = month;
    option.textContent = monthLabel(month, true);
    return option;
}

/**
 * Height of the site header while it is stuck to the top of the screen (theme/topbar.css):
 * what scrolled content must stay below. 0 if it does not stick.
 * @returns {number}
 */
export function stuckHeaderHeight() {
    const header = document.querySelector('.th-topbar');
    if (!header || !['sticky', 'fixed'].includes(window.getComputedStyle(header).position)) {
        return 0;
    }
    return header.offsetHeight;
}

/**
 * Scrolls the page so the element's top is `offset` pixels below the top of the screen.
 * @param {HTMLElement} target
 * @param {number} offset
 */
export function scrollPageTo(target, offset) {
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo(0, Math.max(0, top));
}
